import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import InputMask from "react-input-mask";
import moment from "moment";

import {
  Dialog,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { X as LucideX, CalendarPlus } from "lucide-react";

import { i18n } from "../../translate/i18n";
import usePlans from "../../hooks/usePlans";
import { validateCPFOrCNPJ } from "../../utils/documentValidator";
import { getDocumentMask } from "../../utils/documentFormatter";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CancelButton } from "../ActionButtons";
import SimpleDatePicker from "../SimpleDatePicker";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark =
    theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    dialogPaper: {
      ...(ds.dialogPaper || {}),
      background: isDark ? "#121212" : "#ffffff",
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: ds.closeButton?.backgroundColor || "rgba(255, 255, 255, 0.08)",
      color: ds.closeButton?.color || "#ffffff",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    content: {
      padding: "20px 20px 16px",
      ...(theme.scrollbarStyles || {}),
    },
    field: {
      width: "100%",
    },
    incrementBtn: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "8px 14px",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      color: isDark ? "#e9edef" : "#0f172a",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      transition: "background 0.18s ease",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
      },
    },
    deleteBtn: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "0 18px",
      height: 38,
      border: "none",
      cursor: "pointer",
      color: "#fff",
      background: "#e53935",
      transition: "background 0.18s ease",
      "&:hover": { background: "#c62828" },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
  };
});

const buildEmpty = () => ({
  name: "",
  email: "",
  phone: "",
  planId: "",
  status: true,
  dueDate: "",
  recurrence: "",
  password: "",
  document: "",
  paymentMethod: "",
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t("compaies.form.nameRequired")),
  email: Yup.string().email().required(i18n.t("compaies.form.emailRequired")),
  document: Yup.string()
    .nullable()
    .transform((value, originalValue) => {
      if (!originalValue || originalValue.trim() === "") return null;
      return value;
    })
    .test(
      "cpf-cnpj",
      i18n.t("compaies.form.documentInvalid"),
      (value) => {
        if (!value) return true;
        return validateCPFOrCNPJ(value);
      }
    ),
});

const CompanyModal = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  initialValue,
  loading,
}) => {
  const classes = useStyles();
  const filterDropdownClasses = useFilterDropdownStyles();
  const { list: listPlans } = usePlans();
  const [plans, setPlans] = useState([]);
  const [record, setRecord] = useState(buildEmpty());

  useEffect(() => {
    let cancelled = false;
    async function fetchPlans() {
      try {
        const list = await listPlans();
        if (!cancelled) setPlans(list || []);
      } catch (e) {
        if (!cancelled) setPlans([]);
      }
    }
    if (open) fetchPlans();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const safe = initialValue || buildEmpty();
    let dueDate = safe.dueDate || "";
    if (dueDate && moment(dueDate).isValid()) {
      dueDate = moment(dueDate).format("YYYY-MM-DD");
    }
    setRecord({ ...buildEmpty(), ...safe, dueDate, password: "" });
  }, [initialValue, open]);

  const incrementDueDate = (values, setFieldValue) => {
    if (!values.dueDate || !moment(values.dueDate).isValid()) return;
    const map = { MENSAL: 1, BIMESTRAL: 2, TRIMESTRAL: 3, SEMESTRAL: 6, ANUAL: 12 };
    const months = map[values.recurrence];
    if (!months) return;
    const next = moment(values.dueDate)
      .add(months, "month")
      .format("YYYY-MM-DD");
    setFieldValue("dueDate", next);
  };

  const handleSubmit = (values) => {
    const data = {
      id: values.id,
      name: values.name,
      email: values.email,
      phone: values.phone,
      planId: values.planId,
      status: values.status,
      dueDate: values.dueDate,
      recurrence: values.recurrence,
      password: values.password,
      document: values.document,
      paymentMethod: values.paymentMethod,
    };
    if (!data.dueDate || !moment(data.dueDate).isValid()) data.dueDate = null;

    if (!data.password?.trim()) {
      delete data.password;
    }

    const normalized = {
      ...data,
      document: data.document?.trim()
        ? data.document.replace(/[.\-\/\s]/g, "")
        : undefined,
    };
    onSubmit(normalized);
  };

  const isEditing = !!initialValue?.id;

  const dropdownMenuProps = {
    PaperProps: { className: filterDropdownClasses.paper },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          {isEditing
            ? i18n.t("compaies.modal.editTitle") || "Editar empresa"
            : i18n.t("compaies.modal.createTitle") || "Nova empresa"}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
        >
          <LucideX size={18} strokeWidth={2.5} />
        </IconButton>
      </div>

      <Formik
        enableReinitialize
        initialValues={record}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent dividers className={classes.content}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label={i18n.t("compaies.table.name")}
                    name="name"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label={i18n.t("compaies.table.email")}
                    name="email"
                    type="email"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label={i18n.t("compaies.table.phone")}
                    name="phone"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field name="document">
                    {({ field, form, meta }) => {
                      const mask = getDocumentMask(field.value);
                      return (
                        <InputMask
                          mask={mask}
                          value={field.value || ""}
                          onChange={(e) =>
                            form.setFieldValue("document", e.target.value)
                          }
                          onBlur={field.onBlur}
                        >
                          {(inputProps) => (
                            <TextField
                              {...inputProps}
                              label={i18n.t("compaies.form.documentLabel")}
                              name="document"
                              variant="outlined"
                              size="small"
                              className={classes.field}
                              placeholder={i18n.t("compaies.form.documentPlaceholder")}
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </InputMask>
                      );
                    }}
                  </Field>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    label={i18n.t("compaies.table.password")}
                    name="password"
                    type="password"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    placeholder={isEditing ? "••••••••" : ""}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" size="small" className={classes.field}>
                    <InputLabel>{i18n.t("compaies.table.plan")}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("compaies.table.plan")}
                      name="planId"
                      MenuProps={dropdownMenuProps}
                    >
                      <MenuItem value="">—</MenuItem>
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControl variant="outlined" size="small" className={classes.field}>
                    <InputLabel>{i18n.t("compaies.table.active")}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("compaies.table.active")}
                      name="status"
                      MenuProps={dropdownMenuProps}
                    >
                      <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                      <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SimpleDatePicker
                    value={
                      values.dueDate && moment(values.dueDate).isValid()
                        ? moment(values.dueDate).toDate()
                        : null
                    }
                    onChange={(date) => {
                      setFieldValue(
                        "dueDate",
                        date ? moment(date).format("YYYY-MM-DD") : ""
                      );
                    }}
                    label={i18n.t("compaies.table.dueDate")}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl variant="outlined" size="small" className={classes.field}>
                    <InputLabel>{i18n.t("compaies.table.recurrence")}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("compaies.table.recurrence")}
                      name="recurrence"
                      MenuProps={dropdownMenuProps}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="MENSAL">{i18n.t("compaies.table.monthly")}</MenuItem>
                      <MenuItem value="BIMESTRAL">{i18n.t("compaies.table.bimonthly")}</MenuItem>
                      <MenuItem value="TRIMESTRAL">{i18n.t("compaies.table.quarterly")}</MenuItem>
                      <MenuItem value="SEMESTRAL">{i18n.t("compaies.table.semester")}</MenuItem>
                      <MenuItem value="ANUAL">{i18n.t("compaies.table.yearly")}</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>

                {isEditing && values.dueDate && values.recurrence && (
                  <Grid item xs={12}>
                    <button
                      type="button"
                      className={classes.incrementBtn}
                      onClick={() => incrementDueDate(values, setFieldValue)}
                    >
                      <CalendarPlus size={14} strokeWidth={1.8} />
                      {i18n.t("compaies.modal.incrementDueDate")
                        || "Avançar vencimento conforme recorrência"}
                    </button>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <ChatiaDialogActions
              loading={loading || isSubmitting}
              disabled={loading || isSubmitting}
              saveText={i18n.t("compaies.table.save")}
              type="submit"
            >
              {isEditing && (
                <button
                  type="button"
                  className={classes.deleteBtn}
                  onClick={() => onDelete && onDelete()}
                  disabled={loading || isSubmitting}
                  style={{ marginRight: "auto" }}
                >
                  {i18n.t("compaies.table.delete")}
                </button>
              )}
              <CancelButton onClick={onClose}>
                {i18n.t("compaies.modal.cancel") || "Cancelar"}
              </CancelButton>
            </ChatiaDialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CompanyModal;
